import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, 
  ArrowUpDown, RefreshCw, Warehouse, Building2, Globe, X, Package, Box, Filter 
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة

// استيراد السياقات الموحدة
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';
import { exportToExcel } from './exportUtils'; 


const StoreroomList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('ar') || i18n.language.startsWith('ur');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canCreate, isSuperAdmin } = usePermissions('storerooms'); // 🛡️ الصلاحيات
  
  // --- Session Data ---
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';

  // سياق حجم الخط
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  // --- State ---
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showAllSites, setShowAllSites] = useState(searchParams.get('all_sites') === 'true');

  // --- Dropdowns for Super Admin ---
  const [orgsList, setOrgsList] = useState([]);
  const [sitesList, setSitesList] = useState([]);

  // --- URL Params (Persistence) ---
  const page = parseInt(searchParams.get('page')) || 1;
  const limit = 50;
  const q = searchParams.get('q') || "";
  const locationFilter = searchParams.get('location') || "";
  const description = searchParams.get('description') || "";
  const isdefault = searchParams.get('isdefault') || "";
  const sortBy = searchParams.get('sortBy') || "location";
  const sortDir = searchParams.get('sortDir') || "asc";
  const selectedOrg = searchParams.get('org_id') || "";
  const selectedSite = searchParams.get('site_id') || "";

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const forceAll = isSuperAdmin && (selectedOrg !== "" || selectedSite !== "");
      const effectiveAll = showAllSites || forceAll;
      const effectiveSite = effectiveAll ? selectedSite : sessionSite;

      const res = await axios.get('/api/storerooms', {
        headers: { 'Authorization': `Bearer ${token}` },
        params: { 
          page, limit, search: q, location: locationFilter, description, isdefault,
          site_id: effectiveSite, org_id: selectedOrg || sessionOrg, all_sites: Boolean(effectiveAll),
          sortBy, sortDir 
        }
      });
      
      setLocations(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(t('common.load_error', "Error loading storerooms"));
    } finally {
      setLoading(false);
    }
  }, [page, limit, q, locationFilter, description, isdefault, selectedOrg, selectedSite, showAllSites, sortBy, sortDir, isSuperAdmin, sessionSite, sessionOrg, t]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 100);
    return () => clearTimeout(timer);
  }, [fetchData]);

  // جلب المنظمات للمدير العام
  useEffect(() => {
    if (isSuperAdmin) {
      axios.get('/api/organizations', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      }).then(res => setOrgsList(res.data));
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (selectedOrg) {
      const org = orgsList.find(o => o.org_id === selectedOrg);
      setSitesList(org ? org.sites : []);
    } else { setSitesList([]); }
  }, [selectedOrg, orgsList]);

  // --- Filter Handlers ---
  const updateFilters = (newFilters) => {
    const params = Object.fromEntries(searchParams.entries());
    const updated = { ...params, ...newFilters };
    Object.keys(updated).forEach(key => !updated[key] && delete updated[key]);
    setSearchParams(updated);
  };

  const handleSort = (column) => {
    const direction = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    updateFilters({ sortBy: column, sortDir: direction, page: 1 });
  };

  const handleClearFilters = () => {
    setSearchParams({});
    setShowAllSites(false);
  };

  const handleRowClick = (id) => {
    const activeFilters = Object.fromEntries(searchParams.entries());
    const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
    let navParams = { ...activeFilters, org_id: sessionOrg };
    if (effectiveSite) navParams.site_id = effectiveSite; else delete navParams.site_id;
    if (showAllSites) navParams.all_sites = true;
    
    const queryParams = new URLSearchParams(navParams).toString();
    navigate(`/storerooms/${id}${queryParams ? '?' + queryParams : ''}`);
  };

  // --- Sidebar Events ---
  useEffect(() => {
    const openAllSites = () => { setShowAllSites(true); updateFilters({ page: 1, all_sites: true }); };
    const viewMySite = () => { setShowAllSites(false); updateFilters({ page: 1, all_sites: false }); };
    const exportData = () => {
        const activeFilters = Object.fromEntries(searchParams.entries());
        const effectiveSite = !showAllSites && !activeFilters.site_id ? sessionSite : activeFilters.site_id;
        let exportParams = { ...activeFilters, org_id: sessionOrg, search: q };
        if (effectiveSite) exportParams.site_id = effectiveSite; else delete exportParams.site_id;

        const columnsMap = {
            "location": t('storerooms.fields.location', 'Location'),
            "description": t('assets.headers.desc', 'Summary'),
            "status": t('assets.headers.status', 'Status'),
            "siteid": t('common.site', 'Site'),

        };
        exportToExcel('storerooms', exportParams, columnsMap, t);
    };
    window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
    window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
    window.addEventListener('GLOBAL_EXPORT', exportData);

    return () => {
      window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      window.removeEventListener('GLOBAL_EXPORT', exportData);
    };
  }, [searchParams]);

  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <div className={`flex flex-col h-full bg-surface-app uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* 1. Header & Global Search */}
      <div className="bg-surface-card p-3 border-b shadow-sm z-20 space-y-3">
        <div className="flex justify-between items-center px-4">
          <div className="flex items-center gap-6">
            <h2 className="text-primary font-black italic text-lg flex items-center gap-2">
              <RefreshCw size={22} className={`${loading ? 'animate-spin text-primary' : 'text-primary cursor-pointer hover:rotate-180 transition-all duration-500'}`} onClick={() => fetchData()} title={t('common.refresh', 'Refresh')} />
              <Warehouse size={22} className="text-primary" /> {t('storerooms.list.title', 'Storerooms')}
              <span className={`text-[0.9em] px-2 py-0.5 rounded-full border not-italic font-bold ml-2 ${showAllSites ? 'bg-status-danger-bg text-status-danger border-status-danger/30' : 'bg-primary-soft text-primary border-primary/30'}`}>
                  {showAllSites ? t('common.global_view', 'GLOBAL VIEW') : `${t('common.site', 'SITE')}: ${sessionSite}`}
              </span>
            </h2>
            <div className={`relative group ${isRTL ? 'mr-4' : 'ml-4'}`}>
              <Search className={`absolute top-2 text-ink-secondary group-focus-within:text-primary ${isRTL ? 'right-3' : 'left-3'}`} size={16} />
              <input
                className={`py-1.5 border border-line w-[300px] outline-none focus:border-primary shadow-inner rounded-sm font-black text-primary placeholder:font-normal placeholder:italic uppercase transition-all ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'}`}
                placeholder={t('common.search')} 
                value={q}
                onChange={(e) => updateFilters({ q: e.target.value, page: 1 })}
              />
            </div>
          </div>

          <div className="flex gap-3 items-center">
            <button onClick={handleClearFilters} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors text-[0.9em] uppercase">
              <X size={14}/> {t('common.clear_filters', 'CLEAR FILTERS')}
            </button>

            {/* Org/Site Filters for SuperAdmin */}
            {isSuperAdmin && (
              <div className="flex items-center gap-4 bg-surface-panel p-1.5 rounded-sm border border-line shadow-sm animate-in fade-in">
                <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-l pl-4' : 'border-r pr-4'}`}>
                  <Globe size={14} className="text-primary" />
                  <select className={`bg-transparent outline-none text-[0.8em] font-black uppercase cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`} value={selectedOrg} onChange={(e) => updateFilters({ org_id: e.target.value, site_id: '', page: 1 })}>
                    <option value="">{t('common.all_orgs', 'All Orgs')}</option>
                    {orgsList.map(o => <option key={o.org_id} value={o.org_id}>{o.org_id}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 size={14} className="text-primary" />
                  <select className={`bg-transparent outline-none text-[0.8em] font-black uppercase cursor-pointer ${isRTL ? 'text-right' : 'text-left'}`} value={selectedSite} onChange={(e) => updateFilters({ site_id: e.target.value, page: 1 })} disabled={!selectedOrg}>
                    <option value="">{t('common.all_sites', 'All Sites')}</option>
                    {sitesList.map(s => <option key={s.site_id} value={s.site_id}>{s.site_id}</option>)}
                  </select>
                </div>
              </div>
            )}

            {canCreate && (
              <button onClick={() => navigate('/storerooms/new')} className="bg-primary text-white px-6 py-2 rounded-sm font-bold flex items-center gap-2 text-[0.9em] uppercase hover:bg-primary-dark shadow-md transition-all active:scale-95">
                <Plus size={14} /> {t('storerooms.list.new', 'NEW STOREROOM')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar">
        <div className="bg-surface-card border border-line shadow-sm rounded-sm overflow-hidden min-h-[450px]">
          <table className="w-full text-left border-collapse">
            <thead className="bg-surface-panel border-b border-line text-ink-secondary font-bold uppercase sticky top-0 z-10 shadow-sm tracking-widest text-[0.9em]">
              <tr>
                <SortableHeader label={t('storerooms.fields.location', 'Storeroom')} sortKey="location" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} width="w-48" />
                <SortableHeader label={t('assets.headers.desc', 'Description')} sortKey="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} isRTL={isRTL} />
                <th className={`p-3 border-line text-center w-32 ${isRTL ? 'border-l' : 'border-r'}`}>{t('common.site', 'Site')}</th>
                <SortableHeader label={t('storerooms.fields.isdefault', 'Is Default?')} sortKey="isdefault" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="center" isRTL={isRTL} width="w-32" />
                <th className="p-3 text-center w-24 italic text-[0.8em]">{t('storerooms.fields.inventory', 'Inventory')}</th>
              </tr>
              {/* Filter Row */}
              <tr className="bg-surface-panel">
                <td className="p-1 border-x border-line">
                  <FilterInput value={locationFilter} onChange={v => updateFilters({ location: v, page: 1 })} />
                </td>
                <td className="p-1 border-x border-line">
                  <FilterInput value={description} onChange={v => updateFilters({ description: v, page: 1 })} />
                </td>
                <td className="p-1 border-x border-line bg-surface-panel/50"></td>
                <td className="p-1 border-x border-line">
                    <select className={`w-full p-1.5 text-primary font-bold outline-none bg-transparent cursor-pointer ${isRTL ? 'text-right' : 'text-center'}`} value={isdefault} onChange={e => updateFilters({ isdefault: e.target.value, page: 1 })}>
                        <option value="">{t('common.all', 'ALL')}</option>
                        <option value="true">{t('common.yes', 'YES')}</option>
                        <option value="false">{t('common.no', 'NO')}</option>
                    </select>
                </td>
                <td className="bg-surface-panel border-x border-line"></td>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-subtle">
              {loading ? (
                <tr><td colSpan="5" className="p-20 text-center animate-pulse text-ink-secondary uppercase font-black tracking-widest">{t('common.syncing', 'Loading Storeroom Data...')}</td></tr>
              ) : locations.length === 0 ? (
                <tr><td colSpan="5" className="p-20 text-center text-ink-secondary italic font-black uppercase tracking-widest">{t('common.no_records', 'No records found')}</td></tr>
              ) : locations.map((store) => (
                <tr key={store.id} className={`hover:bg-primary-soft cursor-pointer group transition-all text-[0.9em] ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => handleRowClick(store.location)}>
                  <td className={`p-3 border-line-subtle font-black text-primary uppercase group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>{store.location}</td>
                  <td className={`p-3 border-line-subtle text-ink font-semibold ${isRTL ? 'border-l' : 'border-r'}`}>{store.description}</td>
                  <td className={`p-3 border-line-subtle font-bold text-ink-secondary text-center uppercase ${isRTL ? 'border-l' : 'border-r'}`}>{store.siteid}</td>
                  <td className={`p-3 border-line-subtle text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                    {store.isdefault ? <span className="bg-status-success-bg text-status-success px-2 py-0.5 rounded-sm text-[0.8em] font-black">{t('storerooms.fields.default', 'DEFAULT')}</span> : <span className="text-ink-secondary">-</span>}
                  </td>
                  <td className="p-3 text-center">
                    <Package size={14} className="text-primary mx-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Footer */}
      <div className={`bg-surface-card border-t border-line p-2 px-8 flex justify-between items-center shadow-sm z-30 font-black text-[0.9em] text-ink-secondary rounded-b-sm `}>
        <div className={`flex items-center gap-6 `}>
            <div className={`flex items-center gap-2 `}>
                <Box size={14} className="text-sky"/>
                <span>{t('common.showing', 'Showing')}: <span className="text-primary">{(page-1)*limit+1}-{Math.min(page*limit, total)}</span></span>
            </div>
            <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-r pr-6 flex-row-reverse' : 'border-l pl-6'}`}>
                <Filter size={14} className="text-status-warning"/>
                <span>{t('common.total', 'Total')}: <span className="text-status-warning">{total}</span></span>
            </div>
        </div>

        <div className={`flex items-center gap-4 `}>
            <div className={`flex items-center gap-1 bg-surface-panel rounded px-2 py-1 border border-line `}>
                <button disabled={page === 1} onClick={() => updateFilters({page: 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page === 1} onClick={() => updateFilters({page: page - 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <div className="px-4 text-[11px] font-mono border-x border-line mx-1">
                    <span className="text-primary text-sm">{page}</span> / {totalPages}
                </div>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: page + 1})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button disabled={page >= totalPages} onClick={() => updateFilters({page: totalPages})} className="p-1.5 hover:text-primary disabled:opacity-30 transition-all"><ChevronsRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
        </div>
      </div>
    </div>
  );
};

// --- Helper Components ---
const SortableHeader = ({ label, sortKey, sortBy, sortDir, onSort, align = "left", isRTL, width = "" }) => (
  <th className={`p-3 border-line cursor-pointer hover:bg-surface-panel transition-colors text-${align === 'center' ? 'center' : (isRTL ? 'right' : 'left')} ${isRTL ? 'border-l' : 'border-r'} ${width}`} onClick={() => onSort(sortKey)}>
      <div className={`flex items-center gap-2 ${align === 'center' ? 'justify-center' : 'justify-between'} `}>
          {label}
          <ArrowUpDown size={10} className={sortBy === sortKey ? 'text-primary' : 'text-ink-secondary'} />
      </div>
  </th>
);

const FilterInput = ({ value, onChange, placeholder = "", center = false, className = "" }) => (
  <input
    className={`w-full p-1 text-primary font-bold outline-none bg-transparent placeholder-ink-secondary border border-transparent focus:border-primary focus:bg-surface-card rounded-sm transition-all ${center ? 'text-center' : ''} ${className}`}
    placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} 
  />
);

export default StoreroomList;